import { useEffect, useMemo, useRef, useState } from "react"

// ** Third Party Libraries
import { z } from "zod"
import { Loader } from "lucide-react"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Calendar as CalendarIcon, CloseCircle, TickCircle, Warning2 } from "iconsax-reactjs"

// ** Theme Components
import { Form, FormControl, FormField, FormItem, FormLabel } from "@pma/components/common/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@pma/components/common/select"
import { Calendar } from "@pma/components/common/calendar"
import { MultiSelectDropdown } from "@pma/components/common/multiSelectDropDown"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Services
import { getProjectListService, setProjectReminderService } from "@pma/services/project"
import { getGroupWiseTaskListService } from "@pma/services/task"

// ** Types
import {
  DictType,
  MultiSelectOption,
  NotificationFormProps,
  NotificationFormValues,
  NotificationMethod,
} from "@pma/types"

// ** Constants
import {
  DAYS_OF_WEEK,
  DEFAULT_FORM_VALUES,
  NOTIFICATION_FREQUENCIES,
  NOTIFICATION_METHODS,
} from "@pma/utils/constants"

// ** Helpers & Hooks
import { cn } from "@pma/utils/helpers"
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

const notificationSchema = z
  .object({
    frequency: z.enum(["daily", "weekly", "monthly"]),
    time: z.string().min(1),
    dayOfWeek: z.string().optional(),
    dayOfMonth: z.string().optional(),
    projectId: z.string().min(1),
    excludedUsers: z.array(z.string()).default([]),
    excludedTasks: z.array(z.string()).default([]),
    methods: z.array(z.enum(["whatsapp", "email"])).min(1),
  })
  .superRefine((data, ctx) => {
    if (data.frequency === "weekly" && !data.dayOfWeek) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["dayOfWeek"], message: "required" })
    }
    if (data.frequency === "monthly" && !data.dayOfMonth) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["dayOfMonth"], message: "required" })
    }
  })

// "HH:MM" local time -> minutes-from-midnight in UTC
const localTimeToUtcMinutes = (time: string) => {
  const [h, m] = time.split(":").map((n) => Number(n))
  const local = new Date()
  local.setHours(h, m, 0, 0)
  return local.getUTCHours() * 60 + local.getUTCMinutes()
}

const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const value = `${String(i).padStart(2, "0")}:00`
  const period = i < 12 ? "AM" : "PM"
  const hour12 = i % 12
  const label = `${String(hour12).padStart(2, "0")}:00 ${period}`
  return { value, label }
})

const RequiredMark = () => <span className='text-red-500'>*</span>

const NotificationForm = ({ open, onClose }: NotificationFormProps) => {
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const dir = isHebrew ? "rtl" : "ltr"
  const orgId = authConfig?.last_organization_id

  const userList: MultiSelectOption[] = useMemo(
    () =>
      (authConfig?.organizationUsers || []).map((u: any) => ({
        id: u.id,
        name: u.name,
        subtitle: u.username ? `@${u.username}` : undefined,
      })),
    [authConfig?.organizationUsers],
  )

  const [showCalendar, setShowCalendar] = useState(false)
  const [errorDismissed, setErrorDismissed] = useState(false)
  const calendarRef = useRef<HTMLDivElement | null>(null)

  const form = useForm<NotificationFormValues>({
    resolver: zodResolver(notificationSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  })

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, isSubmitted },
  } = form

  const frequency = watch("frequency")
  const projectId = watch("projectId")
  const methods = watch("methods")
  const dayOfMonth = watch("dayOfMonth")
  const excludedUsers = watch("excludedUsers")
  const excludedTasks = watch("excludedTasks")

  const { data: projectData } = useQuery({
    queryKey: ["notification-form-project-list", orgId],
    queryFn: () =>
      getProjectListService({
        by_organization_id: orgId,
        paging_start_page: 1,
        paging_per_page: 100,
      }),
    enabled: open && !!orgId,
  })
  const projectList: MultiSelectOption[] = useMemo(
    () =>
      (projectData?.items || []).map((p: any) => ({
        id: String(p.id),
        name: p.title || p.name,
      })),
    [projectData],
  )

  const { data: taskData } = useQuery({
    queryKey: ["notification-form-task-list", projectId],
    queryFn: () =>
      getGroupWiseTaskListService({
        project_id: projectId,
        paging_start_page: 1,
        paging_per_page: 100,
      }),
    enabled: open && !!projectId,
  })
  const taskList: MultiSelectOption[] = useMemo(() => {
    const items: MultiSelectOption[] = []
    if (taskData && typeof taskData === "object") {
      Object.keys(taskData).forEach((key) => {
        const bucket = (taskData as any)[key]
        if (Array.isArray(bucket)) {
          bucket.forEach((t: any) => {
            if (t?.id && t?.name) items.push({ id: String(t.id), name: t.name })
          })
        }
      })
    }
    return items
  }, [taskData])

  // Reset whole form whenever the dialog is opened
  useEffect(() => {
    if (open) {
      reset(DEFAULT_FORM_VALUES)
      setShowCalendar(false)
      setErrorDismissed(false)
    }
  }, [open, reset])

  // Reset project-scoped selections when project changes
  useEffect(() => {
    setValue("excludedUsers", [])
    setValue("excludedTasks", [])
  }, [projectId, setValue])

  // Reset frequency-scoped selections when frequency changes
  useEffect(() => {
    setValue("dayOfWeek", "")
    setValue("dayOfMonth", "")
  }, [frequency, setValue])

  // Close calendar popover on outside click
  useEffect(() => {
    if (!showCalendar) return
    const onClickOutside = (e: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setShowCalendar(false)
      }
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [showCalendar])

  const toggleMethod = (value: NotificationMethod) => {
    const next = methods.includes(value) ? methods.filter((m) => m !== value) : [...methods, value]
    setValue("methods", next, { shouldValidate: true })
  }

  const toggleExclusion = (key: "excludedUsers" | "excludedTasks", id: string | number) => {
    const idStr = String(id)
    const current = key === "excludedUsers" ? excludedUsers : excludedTasks
    const next = current.includes(idStr) ? current.filter((x) => x !== idStr) : [...current, idStr]
    setValue(key, next)
  }

  const reminderMutation = useMutation({
    mutationKey: ["set-project-reminder"],
    mutationFn: setProjectReminderService,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Notification form saved"),
        variant: "success",
      })
      onClose()
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: translate(
          error?.response?.data?.message || "Unable to save notification form",
        ),
        variant: "error",
      })
    },
  })

  const onSubmit = (values: NotificationFormValues) => {
    const payload = {
      project_id: Number(values.projectId),
      is_enabled: true,
      send_at_min_utc: localTimeToUtcMinutes(values.time),
      period: values.frequency,
      use_whatsapp: values.methods.includes("whatsapp"),
      use_email: values.methods.includes("email"),
      tasks_id: values.excludedTasks.map((id) => Number(id) || id),
      exclude_users_id: values.excludedUsers.map((id) => Number(id) || id),
      ...(values.frequency === "weekly" && values.dayOfWeek
        ? { day_of_week: Number(values.dayOfWeek) }
        : {}),
      ...(values.frequency === "monthly" && values.dayOfMonth
        ? { day_of_month: Number(values.dayOfMonth) }
        : {}),
    }
    reminderMutation.mutate(payload as any)
  }

  const hasErrors = isSubmitted && Object.keys(errors).length > 0
  const showErrorBanner = hasErrors && !errorDismissed
  const isSaveDisabled = reminderMutation.isPending || hasErrors

  if (!open) return null

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center sm:p-4",
        dir,
      )}
      dir={dir}
      onClick={onClose}
    >
      <div
        className='relative flex max-h-[95vh] w-full flex-col overflow-hidden bg-white shadow-xl sm:max-w-2xl sm:rounded-2xl'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className='flex items-start justify-between border-b border-neutral-200 px-4 py-4 sm:px-6'>
          <div className={isHebrew ? "text-right" : "text-left"}>
            <h2 className='font-[inter] text-base font-semibold text-neutral-800'>
              {translate("Notifications Form")}
            </h2>
            <p className='mt-0.5 font-[inter] text-xs text-neutral-500'>
              {translate("Configure automated notifications for task updates")}
            </p>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='shrink-0 text-neutral-500 hover:text-neutral-800'
            aria-label='Close'
          >
            <CloseCircle size={22} />
          </button>
        </div>

        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit, () => setErrorDismissed(false))}
            className='flex min-h-0 flex-1 flex-col'
          >
            <div className='flex flex-col gap-5 px-4 py-5 sm:px-6'>
              {/* Frequency */}
              <FormField
                control={control}
                name='frequency'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='font-[inter] text-sm font-medium text-neutral-800'>
                      {translate("Notification Frequency")} <RequiredMark />
                    </FormLabel>
                    <FormControl>
                      <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                        {NOTIFICATION_FREQUENCIES.map((opt) => {
                          const selected = field.value === opt.value
                          return (
                            <button
                              key={opt.value}
                              type='button'
                              onClick={() => field.onChange(opt.value)}
                              className={cn(
                                "flex flex-col items-center justify-center rounded-lg border px-3 py-3 text-center transition-colors",
                                selected
                                  ? "border-indigo-300 bg-[#F5F8FFFC]"
                                  : "border-neutral-200 bg-white hover:border-neutral-300",
                              )}
                            >
                              <span className='font-[inter] text-sm font-medium text-neutral-800'>
                                {translate(opt.label)}
                              </span>
                              <span className='mt-0.5 font-[inter] text-xs text-neutral-600'>
                                {translate(opt.subtitle)}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Conditional day/date + Time of Day */}
              <div
                className={cn(
                  "grid grid-cols-1 gap-4",
                  frequency !== "daily" ? "md:grid-cols-2" : "",
                )}
              >
                {frequency === "weekly" && (
                  <FormField
                    control={control}
                    name='dayOfWeek'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='font-[inter] text-sm font-medium text-neutral-800'>
                          {translate("Day of Week")} <RequiredMark />
                        </FormLabel>
                        <FormControl>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger
                              className={cn(
                                "h-10 rounded-lg",
                                errors.dayOfWeek && "border-error-300",
                              )}
                              dir={dir}
                            >
                              <SelectValue placeholder={translate("Select day")} />
                            </SelectTrigger>
                            <SelectContent className='rounded-xl' dir={dir}>
                              {DAYS_OF_WEEK.map((d) => (
                                <SelectItem
                                  key={d.value}
                                  value={String(d.value)}
                                  className='rounded-lg'
                                >
                                  {translate(d.label)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}

                {frequency === "monthly" && (
                  <FormItem>
                    <FormLabel className='font-[inter] text-sm font-medium text-neutral-800'>
                      {translate("Select Date")} <RequiredMark />
                    </FormLabel>
                    <div className='relative' ref={calendarRef}>
                      <button
                        type='button'
                        onClick={() => setShowCalendar((v) => !v)}
                        className={cn(
                          "flex h-10 w-full items-center justify-between rounded-lg border px-3 py-2 text-sm outline-none",
                          errors.dayOfMonth ? "border-error-300" : "border-neutral-200",
                          isHebrew ? "text-right" : "text-left",
                        )}
                      >
                        <span
                          className={cn(
                            "font-[inter]",
                            dayOfMonth ? "text-neutral-800" : "text-neutral-400",
                          )}
                        >
                          {dayOfMonth
                            ? `${translate("Day")} ${dayOfMonth}`
                            : translate("Pick a date")}
                        </span>
                        <CalendarIcon size={16} className='text-neutral-500' />
                      </button>
                      {showCalendar && (
                        <div
                          className={cn(
                            "absolute z-50 mt-1 rounded-lg border border-neutral-200 bg-white p-2 shadow-lg",
                            isHebrew ? "right-0" : "left-0",
                          )}
                        >
                          <Controller
                            control={control}
                            name='dayOfMonth'
                            render={({ field }) => (
                              <Calendar
                                mode='single'
                                selected={
                                  field.value
                                    ? new Date(
                                        new Date().getFullYear(),
                                        new Date().getMonth(),
                                        Number(field.value),
                                      )
                                    : undefined
                                }
                                onSelect={(d) => {
                                  field.onChange(d ? String(d.getDate()) : "")
                                  setShowCalendar(false)
                                }}
                                initialFocus
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                  </FormItem>
                )}

                {/* Time of Day */}
                <FormField
                  control={control}
                  name='time'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='font-[inter] text-sm font-medium text-neutral-800'>
                        {translate("Time of Day")} <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger
                            className={cn("h-10 rounded-lg", errors.time && "border-error-300")}
                            dir={dir}
                          >
                            <SelectValue placeholder={translate("Select time")} />
                          </SelectTrigger>
                          <SelectContent className='rounded-xl' dir={dir}>
                            {TIME_OPTIONS.map((t) => (
                              <SelectItem key={t.value} value={t.value} className='rounded-lg'>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              {/* Project + Users */}
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <FormField
                  control={control}
                  name='projectId'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='font-[inter] text-sm font-medium text-neutral-800'>
                        {translate("Select Project")} <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger
                            className={cn(
                              "h-10 rounded-lg",
                              errors.projectId && "border-error-300",
                            )}
                            dir={dir}
                          >
                            <SelectValue placeholder={translate("Project Name")} />
                          </SelectTrigger>
                          <SelectContent className='rounded-xl' dir={dir}>
                            {projectList.map((p) => (
                              <SelectItem key={p.id} value={String(p.id)} className='rounded-lg'>
                                {p.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormItem>
                  <FormLabel className='font-[inter] text-sm font-medium text-neutral-800'>
                    {translate("Users who will receive these notifications")}
                  </FormLabel>
                  <MultiSelectDropdown
                    options={userList}
                    selected={excludedUsers}
                    onToggle={(id) => toggleExclusion("excludedUsers", id)}
                    onClear={() => setValue("excludedUsers", [])}
                    placeholder={translate("All users within the project")}
                    selectedLabelFormatter={(items) =>
                      `${translate("All users - exclude")} ${items.map((i) => i.name).join(", ")}`
                    }
                  />
                </FormItem>
              </div>

              {/* Filter Tasks */}
              <FormItem>
                <FormLabel className='font-[inter] text-sm font-medium text-neutral-800'>
                  {translate("Filter Tasks - click to exclude")}
                </FormLabel>
                <MultiSelectDropdown
                  options={taskList}
                  selected={excludedTasks}
                  onToggle={(id) => toggleExclusion("excludedTasks", id)}
                  onClear={() => setValue("excludedTasks", [])}
                  placeholder={translate("All tasks within the project")}
                  disabled={!projectId}
                  selectedLabelFormatter={(items) =>
                    `${translate("All tasks - exclude")} ${items.map((i) => i.name).join(", ")}`
                  }
                />
              </FormItem>

              {/* Notification Methods */}
              <div>
                <p className='mb-2 font-[inter] text-sm font-medium text-neutral-800'>
                  {translate("Notification Methods")} <RequiredMark />
                </p>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                  {NOTIFICATION_METHODS.map((m) => {
                    const selected = methods.includes(m.value)
                    return (
                      <button
                        type='button'
                        key={m.value}
                        onClick={() => toggleMethod(m.value)}
                        className={cn(
                          "relative flex flex-col items-center justify-center rounded-lg border px-3 py-3 text-center transition-colors",
                          selected
                            ? m.activeBorder
                            : "border-neutral-200 bg-white hover:border-neutral-300",
                        )}
                      >
                        {selected && (
                          <TickCircle
                            size={16}
                            variant='Bold'
                            className={cn(
                              "absolute top-2",
                              isHebrew ? "left-2" : "right-2",
                              m.titleClass,
                            )}
                          />
                        )}
                        <span className={cn("font-[inter] text-sm font-semibold", m.titleClass)}>
                          {translate(m.label)}
                        </span>
                        <span className={cn("mt-0.5 font-[inter] text-xs", m.subtitleClass)}>
                          {translate(m.subtitle)}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Validation banner (single source of error feedback) */}
              {showErrorBanner && (
                <div className='flex items-center justify-between gap-2 rounded-lg border border-error-300 bg-error-50 px-3 py-2.5'>
                  <div className='flex items-center gap-2'>
                    <Warning2 size={18} className='shrink-0 text-error-500' />
                    <p className='font-[inter] text-sm text-error-600'>
                      {translate(
                        "Please fill out all the fields in the form and select at least one notification method",
                      )}
                    </p>
                  </div>
                  <button
                    type='button'
                    onClick={() => setErrorDismissed(true)}
                    className='shrink-0 text-error-500 hover:text-error-700'
                    aria-label={translate("Dismiss")}
                  >
                    <CloseCircle size={18} />
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className='flex flex-col-reverse items-stretch justify-between gap-3 border-t border-neutral-200 px-4 py-4 sm:flex-row sm:items-center sm:px-6'>
              <button
                type='button'
                onClick={onClose}
                className='rounded-full border border-neutral-200 px-6 py-2 font-[inter] text-sm font-normal text-neutral-800 hover:bg-neutral-50'
              >
                {translate("Cancel")}
              </button>
              <button
                type='submit'
                disabled={isSaveDisabled}
                className={cn(
                  "flex items-center justify-center rounded-full px-6 py-2 font-[inter] text-sm font-medium text-white shadow",
                  isSaveDisabled
                    ? "cursor-not-allowed bg-neutral-400"
                    : "bg-[radial-gradient(109.91%_126.55%_at_13.06%_22.92%,_#7A5AF8_30.95%,_#6938EF_65.87%)] hover:opacity-90",
                )}
              >
                {reminderMutation.isPending ? (
                  <Loader className={cn("animate-spin", isHebrew ? "ml-1" : "mr-1")} size={16} />
                ) : null}
                {translate("Save Notifications Form")}
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}

export default NotificationForm
